import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowForward, VerifiedRounded } from '@mui/icons-material';
import CertificationBadge from '../../components/Certifications/CertificationBadge';
import gitmeLogo from '../../images/logo/gitme-logo.svg';
import {
  getCertificationDisplayName,
  getCertificationValidityCopy,
  getCertificationMeta,
  getCertificationSignalCopy,
  getCertificationVerificationTagline
} from '../../constants/certificationBadgeCopy';
import { sendRequest } from '../../utils/dateHelpers';
import {
  buildMarketingHomeUrl,
  buildCertificationMeaningUrl,
  buildCertificationSignupUrl
} from '../../utils/certificationLinks';

const CertificationProofPage = () => {
  const { proofId = '' } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [badge, setBadge] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchProof = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await sendRequest(
          `/certifications/public?proofID=${encodeURIComponent(
            proofId
          )}&view=full`
        );

        if (!isMounted) {
          return;
        }

        setBadge(response?.data || null);
      } catch (fetchError) {
        if (isMounted) {
          setError(fetchError?.message || 'Unable to load this proof.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (proofId) {
      fetchProof();
    } else {
      setLoading(false);
      setError('Proof ID is missing.');
    }

    return () => {
      isMounted = false;
    };
  }, [proofId]);

  const signupUrl = useMemo(
    () => buildCertificationSignupUrl(proofId),
    [proofId]
  );
  const displayName = useMemo(
    () => getCertificationDisplayName(badge),
    [badge]
  );
  const validityCopy = useMemo(
    () => getCertificationValidityCopy(badge),
    [badge]
  );
  const meta = useMemo(() => getCertificationMeta(badge), [badge]);
  const signalCopy = useMemo(() => getCertificationSignalCopy(badge), [badge]);
  const verificationTagline = useMemo(
    () => getCertificationVerificationTagline(badge),
    [badge]
  );
  const meaningUrl = useMemo(
    () => buildCertificationMeaningUrl(proofId),
    [proofId]
  );
  const marketingHomeUrl = useMemo(() => buildMarketingHomeUrl(), []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-4 py-8 text-slate-900 lg:flex lg:items-center">
        <div className="mx-auto grid w-full max-w-[1280px] animate-pulse gap-8 lg:min-h-[calc(100vh-4rem)] lg:grid-cols-[minmax(0,38rem)_minmax(0,42rem)] lg:items-center lg:justify-between lg:gap-8">
          <div className="mx-auto flex h-full w-full max-w-[38rem] items-center justify-center">
            <div className="w-full rounded-[40px] bg-[radial-gradient(circle_at_center,_#f8fafc,_#eef2ff)] p-10">
              <div className="mx-auto aspect-[320/230] w-full max-w-[28rem] rounded-[32px] bg-slate-200/70" />
            </div>
          </div>

          <div className="flex h-full w-full flex-col justify-center">
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-5">
                <div className="h-10 w-80 rounded-full bg-slate-200/80" />
                <div className="h-14 w-64 rounded-2xl bg-slate-200/80" />
                <div className="h-10 w-[28rem] rounded-2xl bg-slate-200/70" />
                <div className="h-1 w-24 rounded-full bg-slate-200/70" />
              </div>

              <div className="space-y-3 pt-2">
                <div className="h-6 w-72 rounded-xl bg-slate-200/70" />
                <div className="h-16 w-48 rounded-[28px] bg-slate-200/80" />
                <div className="flex flex-wrap gap-3 pt-2">
                  <div className="h-12 w-64 rounded-full bg-slate-200/80" />
                  <div className="h-12 w-64 rounded-full bg-slate-200/70" />
                </div>
                <div className="h-5 w-full max-w-[34rem] rounded-lg bg-slate-200/60" />
                <div className="h-5 w-full max-w-[30rem] rounded-lg bg-slate-200/50" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !badge) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10 text-slate-900">
        <div className="max-w-xl rounded-[32px] border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-2xl font-semibold">Proof unavailable</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            {error || 'This certification proof could not be found.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-white px-4 py-8 text-slate-900 lg:flex lg:items-center">
      <div className="mx-auto grid w-full max-w-[1440px] gap-8 lg:min-h-[calc(100vh-4rem)] lg:grid-cols-[minmax(0,48rem)_minmax(0,40rem)] lg:items-center lg:justify-between lg:gap-6">
        <div className="mx-auto flex h-full w-full max-w-[48rem] items-center justify-center">
          <CertificationBadge
            badge={badge}
            companyName={badge.company_name || 'GitMe Company'}
            variant="public"
          />
        </div>

        <div className="flex h-full w-full flex-col justify-center">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-5">
              <div
                className="inline-flex w-fit items-center gap-2 rounded-full px-5 py-2.5"
                style={{ backgroundColor: meta.accent.soft }}
              >
                <VerifiedRounded
                  sx={{ fontSize: 16, color: meta.accent.base }}
                />
                <span
                  className="text-[10px] font-black uppercase tracking-[0.2em]"
                  style={{ color: meta.accent.base }}
                >
                  GitMe Certified Achievement
                </span>
              </div>
              <h2 className="text-[34px] font-black leading-[0.96] tracking-[-0.04em] text-slate-950 sm:text-[48px]">
                {badge.company_name || 'GitMe Company'}
              </h2>
              <h1
                className="text-[22px] font-bold leading-tight tracking-tight sm:whitespace-nowrap sm:text-[28px]"
                style={{ color: meta.accent.base }}
              >
                {displayName}
              </h1>
              <div
                className="h-[4px] w-24 rounded-full"
                style={{
                  background: `linear-gradient(90deg, ${meta.accent.base}, transparent)`
                }}
              />
            </div>

            <div className="max-w-[44rem] space-y-3 pt-2 text-sm leading-7 text-slate-400">
              <p className="text-[14px] font-medium text-slate-500">
                {verificationTagline}
              </p>
              <a
                href={marketingHomeUrl}
                aria-label="Visit GitMe website"
                className="inline-flex w-fit items-center justify-center rounded-2xl border-2 bg-white px-4 py-2.5 shadow-md transition hover:shadow-lg"
                style={{ borderColor: meta.accent.base }}
              >
                <img src={gitmeLogo} alt="GitMe" className="h-7 w-auto" />
              </a>
              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:flex-wrap">
                <a
                  href={signupUrl}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  Get your own certification
                  <ArrowForward sx={{ fontSize: 16 }} />
                </a>
                <a
                  href={meaningUrl}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 transition hover:bg-slate-100"
                >
                  {signalCopy.buttonLabel}
                </a>
              </div>
              {validityCopy ? <p>{validityCopy}</p> : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificationProofPage;
